import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Botao } from '../../componentes/ui';
import {
  cancelarAcao,
  confirmarAcao,
  type AcaoDaBeca,
  type SituacaoDaAcao,
} from '../../servicos/beca';
import { mensagemDoErro } from '../../servicos/erros';

const MOLDURA: Readonly<Record<SituacaoDaAcao, string>> = {
  PENDENTE: 'border-acento-indigo/40 bg-acento-indigo/5',
  EXECUTANDO: 'border-acento-indigo/40 bg-acento-indigo/5',
  EXECUTADA: 'border-economia/40 bg-economia/5',
  FALHOU: 'border-erro/40 bg-erro/5',
  CANCELADA: 'border-borda bg-superficie-vidro/40',
  EXPIRADA: 'border-borda bg-superficie-vidro/40',
};

const ROTULO: Readonly<Record<SituacaoDaAcao, { texto: string; cor: string }>> = {
  PENDENTE: { texto: 'Para você confirmar', cor: 'text-acento-indigo' },
  EXECUTANDO: { texto: 'Fazendo…', cor: 'text-acento-indigo' },
  EXECUTADA: { texto: '✓ Feito', cor: 'text-economia' },
  FALHOU: { texto: 'Não deu certo', cor: 'text-erro' },
  CANCELADA: { texto: 'Cancelada — nada foi feito', cor: 'text-conteudo-sutil' },
  EXPIRADA: { texto: 'Expirou — nada foi feito', cor: 'text-conteudo-sutil' },
};

/**
 * As ações que a BECA preparou numa resposta (D-014): cada uma num cartão, com
 * o que vai acontecer e os botões. Nada acontece sem o "Confirmar".
 *
 * "Confirmar todas" confirma uma de cada vez, na ordem: duas ações sobre o
 * mesmo card não podem correr ao mesmo tempo.
 */
export function AcoesDaResposta(props: {
  readonly acoes: readonly AcaoDaBeca[];
  readonly conversaId: string | null;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  /** A ação em andamento e o que acabou de acontecer — antes de a conversa recarregar. */
  const [ocupada, setOcupada] = useState<string | null>(null);
  const [atualizadas, setAtualizadas] = useState<Readonly<Record<string, AcaoDaBeca>>>({});
  const [erros, setErros] = useState<Readonly<Record<string, string>>>({});
  const [emLote, setEmLote] = useState(false);

  if (props.acoes.length === 0) return null;
  const acoes = props.acoes.map((a) => atualizadas[a.id] ?? a);
  const pendentes = acoes.filter((a) => a.situacao === 'PENDENTE');

  const decidir = async (a: AcaoDaBeca, confirmar: boolean): Promise<void> => {
    setOcupada(a.id);
    setErros(({ [a.id]: _antigo, ...resto }) => resto);
    try {
      const r = await (confirmar ? confirmarAcao(a.id) : cancelarAcao(a.id));
      setAtualizadas((atual) => ({ ...atual, [a.id]: r }));
    } catch (erro) {
      setErros((atual) => ({ ...atual, [a.id]: mensagemDoErro(erro) }));
    } finally {
      setOcupada(null);
    }
  };

  const depois = (): void => {
    // A ação mudou o Deca: as outras telas buscam de novo quando forem abertas.
    void queryClient.invalidateQueries();
    if (props.conversaId !== null) {
      void queryClient.invalidateQueries({ queryKey: ['beca', 'conversa', props.conversaId] });
    }
  };

  const confirmarTodas = async (): Promise<void> => {
    setEmLote(true);
    for (const a of pendentes) await decidir(a, true);
    setEmLote(false);
    depois();
  };

  return (
    <div className="mt-3 flex flex-col gap-2">
      {pendentes.length > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-acento-indigo/30 px-3 py-2">
          <span className="text-sm text-conteudo-secundario">
            {pendentes.length} ações para confirmar
          </span>
          <Botao
            tamanho="pequeno"
            carregando={emLote}
            disabled={ocupada !== null}
            onClick={() => void confirmarTodas()}
          >
            Confirmar todas
          </Botao>
        </div>
      )}
      {acoes.map((a) => (
        <CartaoDeAcao
          key={a.id}
          acao={a}
          erro={erros[a.id] ?? null}
          ocupada={ocupada === a.id}
          bloqueada={emLote || (ocupada !== null && ocupada !== a.id)}
          aoConfirmar={async () => {
            await decidir(a, true);
            depois();
          }}
          aoCancelar={async () => {
            await decidir(a, false);
            depois();
          }}
        />
      ))}
    </div>
  );
}

function CartaoDeAcao(props: {
  readonly acao: AcaoDaBeca;
  readonly erro: string | null;
  readonly ocupada: boolean;
  readonly bloqueada: boolean;
  readonly aoConfirmar: () => Promise<void>;
  readonly aoCancelar: () => Promise<void>;
}): JSX.Element {
  const a = props.acao;
  const rotulo = ROTULO[a.situacao];
  const d = a.detalhe;

  return (
    <div className={`flex flex-col gap-2 rounded-card border px-3 py-3 ${MOLDURA[a.situacao]}`}>
      <span className={`text-xs font-semibold uppercase tracking-wide ${rotulo.cor}`}>
        {rotulo.texto}
      </span>
      <p className="text-sm font-medium text-conteudo-primario">{a.resumo}</p>

      {d !== null && (d.campos?.length ?? 0) > 0 && (
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
          {d.campos!.map((c) => (
            <div key={c.rotulo} className="contents">
              <dt className="text-conteudo-sutil">{c.rotulo}</dt>
              <dd className="break-words text-conteudo-primario">{c.valor}</dd>
            </div>
          ))}
        </dl>
      )}
      {d?.texto !== undefined && (
        <details className="rounded-card border border-borda bg-superficie-elevada">
          <summary className="cursor-pointer px-3 py-2 text-sm text-conteudo-secundario">
            Ver o texto completo
          </summary>
          <p className="whitespace-pre-wrap border-t border-borda px-3 py-2 text-sm text-conteudo-primario">
            {d.texto}
          </p>
        </details>
      )}
      {(d?.avisos?.length ?? 0) > 0 && (
        <ul className="flex flex-col gap-1 text-sm text-atencao">
          {d!.avisos!.map((aviso) => (
            <li key={aviso} className="flex gap-2">
              <span aria-hidden="true">⚠</span>
              <span>{aviso}</span>
            </li>
          ))}
        </ul>
      )}

      {a.resultado !== null && a.situacao !== 'PENDENTE' && (
        <p
          className={`text-sm ${a.situacao === 'FALHOU' ? 'text-erro' : 'text-conteudo-secundario'}`}
        >
          {a.resultado}
        </p>
      )}
      {props.erro !== null && <p className="text-sm text-erro">{props.erro}</p>}

      {a.situacao === 'PENDENTE' && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Botao
            tamanho="pequeno"
            carregando={props.ocupada}
            disabled={props.bloqueada}
            onClick={() => void props.aoConfirmar()}
          >
            Confirmar
          </Botao>
          <Botao
            tamanho="pequeno"
            variante="secundario"
            disabled={props.ocupada || props.bloqueada}
            onClick={() => void props.aoCancelar()}
          >
            Cancelar
          </Botao>
          <span className="text-xs text-conteudo-sutil">
            vale até{' '}
            {new Date(a.validaAte).toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      )}
    </div>
  );
}
